import { format as formatDate, subDays } from "date-fns"
import { useDataProvider } from "../../../data_provider/StandardDataProvider"
import DatePicker, { useDateRangeSelectionState } from '../../../date_picker/DateRangePicker'

import { Alert, Avatar, Badge, Card, Center, ColorSwatch, Divider, Grid, Group, JsonInput, Loader, ScrollArea, Stack, Text, Textarea } from "@mantine/core"
import { useEffect, useState } from "react"
import { EventDetailedView } from "./EventDetailedView"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBars, faClock } from "@fortawesome/free-solid-svg-icons"
import { getEventId, setEventId } from "../../../../utils/UrlLocation"
import { useOutletContext } from "react-router-dom"
import { formatInTimeZone } from "date-fns-tz"



export const EventView = ({selectedSiteId}) => {

    const [startDate, endDate, updateDateRange] = useDateRangeSelectionState({useUrlParam: true})
    const [selectedEvent, setSelectedEvent] = useState(null)
    const updateSelectedEvent = (event) => {
        setSelectedEvent(event)
        if (event) setEventId(event._id)
        // Only remove eventId when an event is already selected
        //  Prevents resetting eventId on initial load
        else if (selectedEvent) setEventId(null)
    }
    
    const {data, requestId, isDataReady, isLoading, isError, errorMessage, lastRefreshTs, refreshData, autoRefreshEnabled, enableAutoRefresh, disableAutoRefresh} = useDataProvider({
        requestMethod: "GET",
        requestUrl: "/api/dashboard/tron/data/events",
        buildRequestBody: () => Object({
            siteId: selectedSiteId,
            startTime: startDate,
            endTime: endDate
        }),
        resetDataOnRequest: false,
        onSuccessFn: (response) => {
            // Works only when the URL event is present in the events query
            if (!selectedEvent) {
                const urlEventID = getEventId()
                const event = response.data.open.concat(response.data.closed).find(x => x._id===urlEventID)
                if (event) setSelectedEvent(event)
                else if (!selectedEvent) setEventId(null)
            }
        }
    })
    

    useEffect(() => {
        refreshData()
        updateSelectedEvent(null)
    }, [selectedSiteId, startDate, endDate])


    return (
        <Grid style={{height: "100%", overflow: "auto"}}>
            <Grid.Col span={3} style={{height: "100%", overflow: "auto"}}>
                <Stack style={{height: "100%", overflow: "auto"}}>
                    <Stack spacing="xs">
                        <Text weight={500}>Filters</Text>
                        <Text size="sm">Event Date Range</Text>
                        <DatePicker
                            startDate={startDate}
                            endDate={endDate}
                            onValueChange={(start, end) => updateDateRange(start, end)}
                            containerProps={{
                                appearance: "default"
                            }}
                        />
                    </Stack>
                    <Text weight={500}>Events</Text>
                    <Divider />
                    {isLoading &&
                        <Center><Loader variant="bars" color="red" /></Center>
                    }
                    {isError &&!isLoading &&
                        <Center><Alert color="red">{errorMessage}</Alert></Center>
                    }
                    {isDataReady &&
                    <ScrollArea>
                        {data.open.map(x => <EventCard event={x} isOpen selectedEvent={selectedEvent} setSelectedEvent={updateSelectedEvent} />)}
                        {data.closed.map(x => <EventCard event={x} selectedEvent={selectedEvent} setSelectedEvent={updateSelectedEvent} />)}
                        {data.open.concat(data.closed).length===0 && <Center><Text>No events found.</Text></Center>}
                    </ScrollArea>
                    }
                </Stack>
            </Grid.Col>
            <Grid.Col span={9} style={{height: "100%"}}>
                <EventDetailedView
                    selectedEvent={selectedEvent}
                />
            </Grid.Col>
        </Grid>
    )

}

const EventCard = ({event, isOpen, selectedEvent, setSelectedEvent}) => {

    const [severityNumMW, severityNumMWFormatted] = getEventMagnitude(event)

    const { assetFrameworkServices } = useOutletContext()
    const site = assetFrameworkServices.getSites(event.assets?.site)
    const timezone = site?.meta?.timezone || "UTC"

    const openedTs = new Date(event.opened_ts)
    const closedTs = new Date(event.closed_ts)
    const openedTsFormatted = !isNaN(openedTs) ? formatInTimeZone(openedTs, timezone, "MMM dd, hh:mm a") : "Invalid Date"
    const closedTsFormatted = !isNaN(closedTs) ? formatInTimeZone(closedTs, timezone, "MMM dd, hh:mm a") : "Invalid Date"

    let className = "phar-event-card"
    if (event._id===selectedEvent?._id) className+=" active"

    const durationSec = !isOpen ? event.duration : (openedTs ? new Date() - openedTs : 0) / 1000
    const durationFormatted = formatDuration(durationSec)
    
    return (
        <Card className={className} mb={10} py={10} pl={10} pr={10} withBorder onClick={() => setSelectedEvent(event)}>
            <Group spacing="xs">
                <ColorSwatch color={isOpen ? "red" : "green"} size={10} />
                <Stack spacing={4} style={{flexGrow: 1}}>
                    <Group style={{justifyContent: "space-between"}}>
                        <Badge color={event.blueprint?.template?.color ? event.blueprint.template.color : "gray"} variant="light" size="sm">{event.blueprint.template.code}</Badge>
                        <Text style={{backgroundColor: getSeverityColor(severityNumMW), paddingLeft: "8px", paddingRight: "8px", borderRadius: "8px"}}>
                            <Group spacing={0} align="baseline">
                                <Text weight={500} color="white" style={{fontSize: "14px", marginRight: "5px"}}>{severityNumMWFormatted}</Text>
                                <Text color="white" style={{fontSize: "10px", fontStyle: "italic"}}>MW</Text>
                            </Group>
                        </Text>
                    </Group>
                    <Group ml={4} style={{justifyContent: "space-between", fontSize: "10px"}}>
                        <Stack spacing={0}>
                            <Group style={{fontSize: "inherit", gap: 5}}>
                                <Text size="xs" color="dimmed" style={{fontSize: "inherit", width: "34px"}}>Opened</Text>
                                <Text size="xs" color="dimmed">-</Text>
                                <Text size="xs" color="dimmed" style={{fontSize: "inherit"}}>{openedTsFormatted}</Text>
                            </Group>
                            <Group style={{fontSize: "inherit", gap: 5}}>
                                <Text size="xs" color="dimmed" style={{fontSize: "inherit", width: "34px"}}>Closed</Text>
                                <Text size="xs" color="dimmed">-</Text>
                                {!isOpen && <Text size="xs" color="dimmed" style={{fontSize: "inherit"}}>{closedTsFormatted}</Text>}
                            </Group>
                        </Stack>
                        <Stack spacing={4} style={{fontSize: "inherit", alignItems: "baseline", alignItems: "flex-end"}}>
                            <Text size="xs">{durationFormatted}</Text>
                            <Group spacing={4}>
                                <FontAwesomeIcon icon={faClock} />
                                {durationSec > 3600 && <FontAwesomeIcon icon={faClock} />}
                                {durationSec > 3600*1.5 && <FontAwesomeIcon icon={faClock} />}
                            </Group>
                        </Stack>
                    </Group>
                </Stack>
            </Group>
        </Card>
    )
}

export default EventView











const getEventMagnitude = (event) => {
    let magnitudeMW
    try {
        const eventDetail = JSON.parse(event.detail)
        magnitudeMW = eventDetail.user_context?.severity?.raw
    } catch {}
    const magnitudeMWFormatted = typeof(magnitudeMW)==="number" ? magnitudeMW.toFixed(1) : "..."
    return [magnitudeMW, magnitudeMWFormatted]
}


const formatDistance = (beginTs, endTs) => {
    const durationMs = endTs - beginTs
    const durationSec = durationMs / 1000
    return formatDuration(durationSec)
}
const formatDuration = (durationSec) => {    
    const durationMin = durationSec / 60
    const durationHr = durationMin / 60
    if (durationMin < 60) return `${Math.round(durationMin)} min.`
    else if (durationHr < 24) return `${Math.floor(durationHr)} hrs ${Math.round(durationMin) - Math.floor(durationHr)*60} min.`
    else return `${Math.floor(durationHr / 24)} days ${Math.round(durationHr % 24)} hrs`
}


const SEVERITY_COLOR_UP_THRESH = 5

const getSeverityColor = (severityMW) => {
    if (severityMW===null || severityMW===undefined) return "#00000050"
    const severityMWPct = severityMW / SEVERITY_COLOR_UP_THRESH
    return getGradientColor(severityMWPct, "#AAF543", "#F57671")
}

const getGradientColor = (percentage, color1Hex, color2Hex) => {
    percentage = Math.max(0, Math.min(1, percentage)) * 100

    const hexToRgb = (hex) => {
        const r = parseInt(hex.substring(1, 3), 16);
        const g = parseInt(hex.substring(3, 5), 16);
        const b = parseInt(hex.substring(5, 7), 16);
        return [r, g, b];
    };

    const rgb1 = hexToRgb(color1Hex);
    const rgb2 = hexToRgb(color2Hex);

    const interpolatedRgb = rgb1.map((c1, i) => {
        const c2 = rgb2[i];
        return Math.round(c1 + (c2 - c1) * (percentage / 100));
    });

    const rgbToHex = (rgb) => {
        return '#' + rgb.map(c => c.toString(16).padStart(2, '0')).join('');
    };

    return rgbToHex(interpolatedRgb);
}